import { initializeApp, FirebaseApp } from 'firebase/app';
import { getAuth, Auth, sendPasswordResetEmail as firebaseSendPasswordResetEmail, User } from 'firebase/auth';
import { initializeFirestore, persistentLocalCache, Firestore } from 'firebase/firestore';
import { getStorage, FirebaseStorage } from 'firebase/storage';
import { getAnalytics, Analytics, isSupported } from 'firebase/analytics';
import { validateFirebaseConfig } from './validation';
import { errorHandler } from '../utils/error/errorHandler';

interface FirebaseServices {
  app: FirebaseApp | null;
  auth: Auth | null;
  db: Firestore | null;
  storage: FirebaseStorage | null;
  analytics: Analytics | null;
}

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: "shopiweb-manager.firebasestorage.app",
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
  measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID
};

let app: FirebaseApp;
let auth: Auth | null = null;
let db: Firestore | null = null;
let storage: FirebaseStorage | null = null;
let analytics: Analytics | null = null;

try {
  // Initialize Firebase app first
  app = initializeApp(firebaseConfig);

  // Initialize services
  auth = getAuth(app);
  db = initializeFirestore(app, {
    cache: persistentLocalCache()
  });
  storage = getStorage(app);

  // Initialize analytics if supported
  isSupported().then(supported => {
    if (supported) {
      analytics = getAnalytics(app);
    }
  });

} catch (error) {
  console.error('Error initializing Firebase:', error);
  errorHandler.handleError(error as Error, 'critical', { context: 'Firebase initialization' });
}

export const sendPasswordResetEmail = async (email: string): Promise<void> => {
  if (!auth) throw new Error('Auth not initialized');
  
  try {
    // Utiliser l'API de base sans options supplémentaires pour éviter les problèmes de domaine
    await firebaseSendPasswordResetEmail(auth, email);
  } catch (error) {
    console.error('Error sending password reset email:', error);
    throw error;
  }
};

export { app, auth, db, storage, analytics };